import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './FeedbackButton.css';

const FeedbackButton: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Prefill email when user is logged in
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (!isOpen || submitted) return;

    const getNextImageNumber = (current: File[]) => {
      let max = 0;
      for (let i = 0; i < current.length; i += 1) {
        const match = /^image(\d+)\./i.exec(current[i].name || '');
        if (!match) continue;
        const n = Number(match[1]);
        if (!Number.isNaN(n)) max = Math.max(max, n);
      }
      return max + 1;
    };

    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as Node | null;
      if (!modalRef.current || !target || !modalRef.current.contains(target)) return;

      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;

      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (!file) continue;

        const isImage = (file.type || '').startsWith('image/');
        if (isImage) {
          setAttachments((prev) => {
            const next = getNextImageNumber(prev);
            const ext = file.type === 'image/jpeg' ? 'jpg' : 'png';
            const named = new File([file], `image${next}.${ext}`, { type: file.type || 'image/png' });
            return [...prev, named];
          });
        } else {
          const filename = file.name && file.name.trim() ? file.name : `arquivo-${Date.now()}`;
          const named = !file.name ? new File([file], filename, { type: file.type || '' }) : file;
          setAttachments((prev) => [...prev, named]);
        }

        setError('');
        e.preventDefault();
        break;
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, submitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const trimmedMessage = feedback.trim();
      if (!trimmedMessage) {
        setError(t('feedback.errorRequired'));
        return;
      }

      for (const file of attachments) {
        if (file.size > 15 * 1024 * 1024) {
          setError(t('feedback.errorFileTooLarge', { name: file.name }));
          return;
        }
      }

      const formData = new FormData();
      if (email.trim()) formData.append('email', email.trim());
      formData.append('message', trimmedMessage);
      formData.append('page_url', window.location.href);
      formData.append('user_agent', navigator.userAgent);
      attachments.forEach((file) => formData.append('files', file));

      await api.post('/feedback/with-attachment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setFeedback('');
        setEmail('');
        setAttachments([]);
      }, 2000);
    } catch (err: any) {
      console.error('Failed to submit feedback:', err);
      setError(err.response?.data?.detail || t('feedback.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const removeAttachmentAt = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className="feedback-fab"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('feedback.open')}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="feedback-modal" ref={modalRef}>
          {submitted ? (
            <div className="feedback-success">
              <div className="feedback-success-icon">✓</div>
              <h3>{t('feedback.successTitle')}</h3>
              <p>{t('feedback.successMessage')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="feedback-form">
              <h3 className="feedback-title">{t('feedback.title')}</h3>
              <p className="feedback-subtitle">
                {t('feedback.subtitle')}
              </p>

              {!user && (
                <div className="feedback-field">
                  <label htmlFor="feedback-email">{t('feedback.emailLabel')}</label>
                  <input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('feedback.emailPlaceholder')}
                    className="feedback-input"
                  />
                </div>
              )}

                <div className="feedback-field">
                  <label htmlFor="feedback-message">{t('feedback.messageLabel')}</label>
                  <textarea
                    id="feedback-message"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder={t('feedback.messagePlaceholder')}
                    rows={6}
                    required
                    className="feedback-textarea"
                  />
                </div>

              <div className="feedback-field">
                <label htmlFor="feedback-attachment">{t('feedback.attachmentLabel')}</label>
                <div className="feedback-attachment-actions">
                  <input
                    id="feedback-attachment"
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    multiple
                    className="feedback-file-input"
                    disabled={submitting}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setAttachments((prev) => [...prev, ...files]);
                      }
                      e.currentTarget.value = '';
                    }}
                  />
                  <label htmlFor="feedback-attachment" className="feedback-file-button">
                    <span className="feedback-file-button-icon" aria-hidden="true">
                      <Paperclip size={16} />
                    </span>
                    <span className="feedback-file-button-label">{t('feedback.attachFile')}</span>
                  </label>
                  <div className="feedback-file-hint">{t('feedback.attachHint')}</div>
                </div>
                {attachments.length > 0 && (
                  <div className="feedback-files-list">
                    {attachments.map((file, index) => (
                      <button
                        key={`${file.name}-${file.size}-${index}`}
                        type="button"
                        className="feedback-file-item"
                        onClick={() => removeAttachmentAt(index)}
                        disabled={submitting}
                        title={t('feedback.removeAttachment')}
                      >
                        <span className="feedback-file-item-x">×</span>
                        <span className="feedback-file-item-name">{file.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div className="feedback-error">
                  {error}
                </div>
              )}

              <div className="feedback-actions">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="feedback-btn feedback-btn-cancel"
                  disabled={submitting}
                >
                  {t('feedback.cancel')}
                </button>
                <button
                  type="submit"
                  className="feedback-btn feedback-btn-submit"
                  disabled={submitting || !feedback.trim()}
                >
                  {submitting ? (
                    t('feedback.sending')
                  ) : (
                    <>
                      <Send size={16} />
                      {t('feedback.send')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
